import {
    type FC,
    type ReactNode,
    type ComponentProps,
    useCallback,
    useRef,
    useMemo,
    type MutableRefObject,
} from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';

import { ItemTile } from 'dibs-search-product-tile/exports/ItemTile';
import ItemHeartWrapper from 'dibs-portfolio-heart/exports/ItemHeartWrapper';
import FavoritesItemProvider from 'dibs-portfolio-folder/exports/FavoritesItemProvider';
import { type FavoritesItemPropsType } from 'dibs-portfolio-folder/exports/FavoritesItemProvider';
import { getBuyerId } from 'dibs-cookie-jar';
import ItemFolderWrapper from 'dibs-portfolio-folder/exports/ItemFolderWrapper';
import {
    trackingConstants,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
    trackEcommerce,
    trackEvent,
    getUserEventItem,
    trackUserEventItemAddToFavorites,
} from 'dibs-tracking';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { useFolderTracking } from './useFolderTracking';
import { isSellerBrandingRemovalTestVariant } from 'dibs-buyer-layout/exports/sellerBrandingRemovalAbTestHelpers';
import { useClientState } from 'dibs-react-hooks/src/useClientState';

import { type CarouselItemTile_viewer$key } from './__generated__/CarouselItemTile_viewer.graphql';
import { type CarouselItemTile_user$key } from './__generated__/CarouselItemTile_user.graphql';
import { type CarouselItemTile_item$key } from './__generated__/CarouselItemTile_item.graphql';

import styles from './CarouselItemTile.scss';

const { EVENT_SELECT_ITEM, EVENT_VIEW_ITEM_LIST, EVENT_WISHLIST } = eventNameConstants;
const { INTERACTION_TYPE_ITEM_FAVORITING } = interactionTypeConstants;
const { STEP_INTERACTION_ITEM_ADDED, STEP_INTERACTION_ITEM_REMOVED } = stepInteractionConstants;
const { ECOM_PRODUCT_CLICK, ECOM_PRODUCT_IMPRESSION } = trackingConstants;

const viewerFragment = graphql`
    fragment CarouselItemTile_viewer on Viewer
    @argumentDefinitions(
        # if favoritesComponentOverride is provided don't fetch favorites
        fetchFavorites: { type: "Boolean", defaultValue: false }
        userId: { type: "String", defaultValue: "" }
    ) {
        ...ItemTile_viewer

        ... on Viewer @include(if: $fetchFavorites) {
            ...ItemHeartWrapper_viewer
                @arguments(userIds: [$userId], loadPortfolioData: $fetchFavorites)
        }
    }
`;

const userFragment = graphql`
    fragment CarouselItemTile_user on User {
        serviceId
        canShowSellerName
        ...FavoritesItemProvider_user
    }
`;

const itemFragment = graphql`
    fragment CarouselItemTile_item on Item
    @argumentDefinitions(
        # if favoritesComponentOverride is provided don't fetch favorites
        fetchFavorites: { type: "Boolean", defaultValue: false }
        userId: { type: "String", defaultValue: "" }
        isTrade: { type: "Boolean", defaultValue: false }
        priceBookName: { type: "String" }
    ) {
        serviceId
        isFurniture
        firstPhotoWebPath(size: thumb)
        title
        ecommerceTrackingParams
        browseUrl
        categoryCode
        ... on Item @include(if: $fetchFavorites) {
            # This needs to match the fragment in ItemHeartWrapper so that when ItemHeartWrapper is refetched, this is also updated
            heartPortfolioItems: portfolioItems(userId: $userId, portfolioType: HEART) {
                portfolioId
            }
            ...ItemHeartWrapper_item @arguments(userId: $userId, loadPortfolioData: $fetchFavorites)
            ...ItemFolderWrapper_item
                @arguments(userId: $userId, loadPortfolioData: $fetchFavorites)
        }
        ...ItemTile_item @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
        ...FavoritesItemProvider_item
    }
`;

const VERTICAL_OFFSET_IMG = 500;
const SRC_SET_SIZES_TILE = '(max-width:768px) 50vw, (max-width:1024px) 33vw, 25vw';

export const CarouselItemTile: FC<{
    viewer: CarouselItemTile_viewer$key;
    user: CarouselItemTile_user$key | null | undefined;
    item: CarouselItemTile_item$key;
    currency: string;
    authModalLoader: $TSFixMe;
    index: number;
    lazyLoadStartIndex: number;
    tileImageSize?: 'imageXSmall' | 'imageLarge' | 'imageMedium';
    showQuickView?: boolean;
    hideImagePadding?: boolean;
    trackingList?: string;
    showFolder?: boolean;
    showFolderOnHover?: boolean;
    favoritesComponentOverride?: (props: FavoritesItemPropsType) => ReactNode;
    trackedImpressionsRef: MutableRefObject<Set<string>>;
    disableContentPadding?: boolean;
    dataTn?: string;
}> = ({
    viewer: viewerRef,
    user: userRef,
    item: itemRef,
    currency,
    authModalLoader,
    index,
    lazyLoadStartIndex,
    tileImageSize = 'imageXSmall',
    showQuickView = false,
    hideImagePadding = false,
    trackingList,
    showFolder = false,
    favoritesComponentOverride,
    trackedImpressionsRef,
    disableContentPadding = false,
    dataTn,
}) => {
    const environment = useRelayEnvironment();
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);

    const containerRef = useRef(null);

    const showSellerName = user?.canShowSellerName;
    const isClient = useClientState();
    const {
        serviceId: itemId,
        browseUrl,
        categoryCode,
        ecommerceTrackingParams,
        isFurniture,
    } = item || {};

    const userEventItem = useMemo(
        () => getUserEventItem({ id: itemId, amount: ecommerceTrackingParams?.price }),
        [ecommerceTrackingParams, itemId]
    );

    const trackUserAddedItemToFavorites = useCallback(() => {
        if (userEventItem) {
            trackUserEventItemAddToFavorites(environment, [userEventItem]);
        }
    }, [environment, userEventItem]);

    const heartAuthModalShow = useCallback(
        (favoriteItem: $TSFixMe) => {
            authModalLoader
                ?.show({
                    flow: authModalLoader?.constants?.SAVE_ITEM_FLOW,
                })
                .then(favoriteItem);
        },
        [authModalLoader]
    );

    const folderModalShow = useCallback(
        () =>
            new Promise(async (resolve, reject) => {
                const onSuccess = (): Promise<void> =>
                    import(
                        /* webpackChunkName: "AddToFolderModalWrapper" */
                        'dibs-portfolio-folder/src/components/AddToFolderModalWrapper'
                    )
                        .then(exported => exported.default)
                        .then(resolve);

                if (!getBuyerId(document.cookie)) {
                    const { userId } = await authModalLoader?.show({
                        flow: authModalLoader?.constants?.SAVE_ITEM_TO_FOLDER_FLOW,
                        shouldPauseAuthFlow: (state: $TSFixMe) => state.step === 0,
                    });
                    if (!userId) {
                        reject();
                    } else {
                        onSuccess();
                    }
                } else {
                    onSuccess();
                }
            }),
        [authModalLoader]
    );

    const folderAddItemSuccess = useCallback(() => {
        if (authModalLoader.isPaused()) {
            authModalLoader.resume();
        }
        trackUserAddedItemToFavorites();
    }, [authModalLoader, trackUserAddedItemToFavorites]);

    const showCreatorName = !showSellerName && isFurniture;

    const onItemClick = useCallback(() => {
        if (!trackingList) {
            return;
        }

        trackEcommerce({
            type: ECOM_PRODUCT_CLICK,
            eventName: EVENT_SELECT_ITEM,
            trigger: trackingList,
            actionField: { list: trackingList, itemId },
            products: [
                {
                    position: index + 1,
                    categoryCode,
                    category: browseUrl,
                    ...ecommerceTrackingParams,
                },
            ],
        });
    }, [browseUrl, categoryCode, ecommerceTrackingParams, index, itemId, trackingList]);

    const onVisibilityChange = useCallback<
        ComponentProps<typeof VisibilityTracker>['onVisibilityChange']
    >(
        ({ isVisible }) => {
            if (
                !isVisible ||
                !trackingList ||
                !itemId ||
                trackedImpressionsRef.current.has(itemId)
            ) {
                return;
            }

            trackedImpressionsRef.current.add(itemId);
            trackEcommerce({
                type: ECOM_PRODUCT_IMPRESSION,
                eventName: EVENT_VIEW_ITEM_LIST,
                products: [
                    {
                        position: index + 1,
                        list: trackingList,
                        categoryCode: categoryCode,
                        category: browseUrl,
                        ...ecommerceTrackingParams,
                    },
                ],
                trigger: trackingList,
            });
        },
        [
            browseUrl,
            categoryCode,
            ecommerceTrackingParams,
            index,
            trackingList,
            itemId,
            trackedImpressionsRef,
        ]
    );

    const handleFavorited = useCallback(
        ({ type }: { type: 'favorited' | 'unFavorited' }): (() => void) => {
            return (): void => {
                if (type === 'favorited') {
                    trackUserAddedItemToFavorites();
                }
                if (!trackingList) {
                    return;
                }

                const step_interaction_name =
                    type === 'favorited'
                        ? STEP_INTERACTION_ITEM_ADDED
                        : STEP_INTERACTION_ITEM_REMOVED;

                trackEvent(
                    {
                        action: step_interaction_name,
                        label: trackingList,
                        category: INTERACTION_TYPE_ITEM_FAVORITING,
                        isInteractiveEvent: true,
                        additional: {
                            itemId,
                        },
                        eventName: EVENT_WISHLIST,
                        interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
                        step_interaction_name,
                        trigger: trackingList,
                    },
                    null
                );
            };
        },
        [trackingList, itemId, trackUserAddedItemToFavorites]
    );

    const folderTracking = useFolderTracking(trackingList);

    return (
        <div
            ref={containerRef}
            className={styles.item}
            data-tn={dataTn ? `${dataTn}-product-tile` : undefined}
        >
            <VisibilityTracker elementRef={containerRef} onVisibilityChange={onVisibilityChange} />
            <FavoritesItemProvider item={item} user={user} applyMouseEvents>
                {favoriteProps => (
                    <ItemTile
                        useLoFiLazyLoader
                        disableContentMargin
                        disableContentPadding={disableContentPadding}
                        disableBorder
                        hideImagePadding={hideImagePadding}
                        viewer={viewer}
                        item={item}
                        index={index}
                        currency={currency}
                        itemsPerRow={lazyLoadStartIndex}
                        showSellerName={
                            isClient && !isSellerBrandingRemovalTestVariant() && showSellerName
                        }
                        showCreatorName={showCreatorName}
                        onClick={onItemClick}
                        showQuickView={showQuickView}
                        imageSize={tileImageSize}
                        itemSearch={null}
                        imageLoadVerticalOffset={VERTICAL_OFFSET_IMG}
                        srcSetSizes={SRC_SET_SIZES_TILE}
                        showMSKUSwatch={false}
                        showMeasurements={false}
                        showNoReserveBadge={false}
                        favoritesComponent={
                            favoritesComponentOverride?.(favoriteProps) || (
                                <div className={styles.iconsWrapper}>
                                    <div className={styles.icon}>
                                        <ItemHeartWrapper
                                            theme="dark"
                                            itemId={itemId}
                                            userId={user?.serviceId}
                                            viewer={viewer}
                                            item={item}
                                            authModalLoader={authModalLoader}
                                            authModalShow={heartAuthModalShow}
                                            onFavorited={handleFavorited({ type: 'favorited' })}
                                            onUnFavorited={handleFavorited({ type: 'unFavorited' })}
                                        />
                                    </div>
                                    {showFolder && favoriteProps.isFolderVisible && (
                                        <div className={styles.icon}>
                                            <ItemFolderWrapper
                                                theme="dark"
                                                singlePortfolioItem
                                                showPlusIcon
                                                selectedItemImages={[item.firstPhotoWebPath]}
                                                selectedItemTitles={[item.title]}
                                                item={item}
                                                userId={user?.serviceId}
                                                userIds={[user?.serviceId]}
                                                getModalComponent={folderModalShow}
                                                onAddItemSuccess={folderAddItemSuccess}
                                                authModalTrackingLabel={trackingList}
                                                trackingHooks={folderTracking}
                                            />
                                        </div>
                                    )}
                                </div>
                            )
                        }
                    />
                )}
            </FavoritesItemProvider>
        </div>
    );
};
